import { CheckCheck, Clock, MapPin, Paperclip, User } from 'lucide-react'
import { format } from 'date-fns'

const getDateSummary = (todo) => {
  if (!todo.startDate) {
    return 'Set date'
  }
  const start = format(new Date(todo.startDate), 'MMM d, h:mm a')
  const end = todo.endDate ? format(new Date(todo.endDate), 'MMM d, h:mm a') : ''
  const recurrence = todo.recurrence ? ` • ${todo.recurrence}` : ''
  return end ? `${start} → ${end}${recurrence}` : `${start}${recurrence}`
}

const getLocationSummary = (todo) => {
  if (todo.locations?.length) {
    return `${todo.locations.length} locations`
  }
  if (todo.location) {
    return todo.location
  }
  return 'Location'
}

const getReminderSummary = (todo) => {
  if (!todo.reminders?.length) {
    return ''
  }
  return `${todo.reminders.length} reminders`
}

const getAttachmentSummary = (todo) => {
  if (!todo.attachments?.length) {
    return 'Add files'
  }
  return `${todo.attachments.length} files`
}

const getSubtaskCounts = (todo) => {
  const subtasks = todo.subtasks || []
  const total = subtasks.length
  const completed = subtasks.filter((subtask) => subtask.completed).length
  return {
    completed,
    total,
  }
}

const getSubtaskSummary = (todo) => {
  const { completed, total } = getSubtaskCounts(todo)
  return `${completed} / ${total}`
}

const infoTabDefinitions = [
  {
    value: 'subtasks',
    featureKey: 'subtasks',
    label: 'Sub',
    icon: CheckCheck,
    getSummary: getSubtaskSummary,
    getBadge: getSubtaskSummary,
    hasValue: (todo) => (todo.subtasks?.length || 0) > 0,
  },
  {
    value: 'when',
    featureKey: 'dates',
    label: 'When',
    icon: Clock,
    getSummary: (todo) => {
      const dateSummary = getDateSummary(todo)
      const reminderSummary = getReminderSummary(todo)
      return reminderSummary ? `${dateSummary} • ${reminderSummary}` : dateSummary
    },
    getBadge: (todo) => {
      const dateCount = todo.startDate ? 1 : 0
      const reminderCount = todo.reminders?.length || 0
      const total = dateCount + reminderCount
      return total > 0 ? String(total) : undefined
    },
    hasValue: (todo) => Boolean(todo.startDate || todo.reminders?.length),
    isEnabled: (enabledFeatures) => Boolean(enabledFeatures.dates || enabledFeatures.reminders),
  },
  {
    value: 'where',
    featureKey: 'location',
    label: 'Where',
    icon: MapPin,
    getSummary: getLocationSummary,
    getBadge: (todo) => (todo.locations?.length ? String(todo.locations.length) : undefined),
    hasValue: (todo) => Boolean(todo.locations?.length || todo.location),
  },
  {
    value: 'who',
    featureKey: 'assignee',
    label: 'Who',
    icon: User,
    getSummary: (todo) =>
      todo.assignees?.length > 0
        ? todo.assignees.length === 1
          ? todo.assignees[0]
          : `${todo.assignees.length} people`
        : 'Assign',
    getBadge: (todo) => (todo.assignees?.length > 0 ? String(todo.assignees.length) : undefined),
    hasValue: (todo) => (todo.assignees?.length || 0) > 0,
  },
  {
    value: 'attachments',
    featureKey: 'attachments',
    label: 'Attach',
    icon: Paperclip,
    getSummary: getAttachmentSummary,
    getBadge: (todo) => (todo.attachments?.length ? String(todo.attachments.length) : undefined),
    hasValue: (todo) => Boolean(todo.attachments?.length),
  },
]

export const buildInfoTabs = (enabledFeatures, todo) =>
  infoTabDefinitions
    .filter((definition) =>
      definition.isEnabled
        ? definition.isEnabled(enabledFeatures)
        : Boolean(enabledFeatures[definition.featureKey]),
    )
    .map((definition) => ({
      value: definition.value,
      label: definition.label,
      icon: definition.icon,
      summary: definition.getSummary(todo),
      badge: definition.getBadge(todo),
      hasValue: definition.hasValue(todo),
    }))

export const getInitialInfoTabValue = (tabs) => {
  const firstWithValue = tabs.find((tab) => tab.hasValue)
  return firstWithValue?.value || tabs[0]?.value || ''
}

export const toIndexedEntries = (items = []) => {
  const seenCount = new Map()
  return items.map((value, index) => {
    let keyPart
    if (value && typeof value === 'object') {
      if (value.id) {
        keyPart = String(value.id)
      } else if (value.remindAt) {
        keyPart = `${value.remindAt}-${value.method || 'push'}`
      } else {
        keyPart = 'obj'
      }
    } else {
      keyPart = String(value)
    }

    const count = (seenCount.get(keyPart) || 0) + 1
    seenCount.set(keyPart, count)

    return {
      id: `${keyPart}-${count}`,
      value,
      index,
    }
  })
}
